/* global debounce */

if (!customElements.get('predictive-search')) {
  class PredictiveSearch extends HTMLElement {
    constructor() {
      super();
      this.cachedResults = {};
      this.requestSerial = 0;
      this.input = this.querySelector('.js-search-input');
      this.productTypeSelect = this.querySelector('.js-search-product-types');
      this.productTypeInput = document.getElementById('product_type_input');
      this.resetBtn = this.querySelector('.js-search-reset');
      this.results = this.querySelector('.js-search-results');
      this.emptyPanel = this.querySelector('[data-search-empty-panel]');
      this.overlay = this.querySelector('.js-search-overlay');
      this.statusEl = this.querySelector('.js-search-status');
      this.loadingText = this.getAttribute('data-loading-text');
      this.addListeners();
    }

    /**
     * Triggers when the web component is removed from the DOM
     */
    disconnectedCallback() {
      this.close();
    }

    addListeners() {
      this.input.addEventListener('focus', this.handleFocus.bind(this));
      this.input.addEventListener('input', debounce(this.handleInput.bind(this)));
      if (this.productTypeSelect) {
        this.productTypeSelect.addEventListener('change', this.handleProductTypeChange.bind(this));
      }
      this.querySelector('.search').addEventListener('submit', this.handleSubmit.bind(this));
    }

    /**
     * Gets the value of the search input field.
     * @returns {string}
     */
    getQuery() {
      return this.input.value.trim();
    }

    /**
     * Handles search form submit.
     * @param {object} evt - Event object.
     */
    handleSubmit(evt) {
      if (
        !this.getQuery().length
        || this.querySelector('.predictive-search__item[aria-selected="true"]:not(.js-submit)')
      ) {
        evt.preventDefault();
      }
    }

    /**
     * Handles 'input' events on the search field.
     */
    handleInput() {
      // autocomplete=off prevents browser from restoring value after using back button in Safari
      this.input.setAttribute('value', this.input.value);

      const searchTerm = this.getQuery();

      if (!searchTerm.length) {
        if (!this.openEmptyState()) this.close();
        return;
      }

      this.getResults(searchTerm);
    }

    /**
     * Handles a change of product type
     * @param {object} evt - Event object.
     */
    handleProductTypeChange(evt) {
      this.productTypeInput.value = evt.detail.selectedValue;
      const query = this.getQuery();
      if (query.length > 0) {
        this.getResults(query);
      }
    }

    /**
     * Handles 'focus' events on the search field.
     */
    handleFocus() {
      const searchTerm = this.getQuery();
      if (!searchTerm.length) {
        this.openEmptyState();
        return;
      }

      if (this.getAttribute('results') === 'true') {
        this.open();
      } else {
        this.getResults(searchTerm);
      }
    }

    /**
     * Opens the focused-empty panel (Popular/Trending) when the query is
     * empty, if it has content to show. Used on focus with an empty query
     * and when the query is cleared back to empty while still focused.
     * @returns {boolean} Whether the empty panel was opened.
     */
    openEmptyState() {
      if (!this.emptyPanel || this.emptyPanel.childElementCount === 0) return false;

      this.requestSerial += 1;
      this.results.innerHTML = '';
      this.removeAttribute('results');
      this.emptyPanel.hidden = false;
      this.open();
      return true;
    }

    /**
     * Handles 'keydown' events on the custom element.
     * @param {object} evt - Event object.
     */
    handleKeydown(evt) {
      // Let tabs script handle keydown events on the tab buttons.
      if (evt.target.matches('.tablist__tab')) return;

      switch (evt.key) {
        case 'ArrowUp':
        case 'ArrowDown':
          evt.preventDefault();

          if (this.hasAttribute('open')) {
            this.handleResultsNav(evt.key);
          }
          break;

        case 'Enter':
          if (this.selectOption()) evt.preventDefault();
          break;

        case 'Escape':
          // Stop the browser's native type=search Escape-clears-value behavior.
          // That native clear fires a real 'input' event a tick later (debounced
          // handleInput runs after this synchronous close()), and since an empty
          // query now opens the focused-empty panel instead of a no-op close(),
          // the race would immediately reopen the panel this Escape just closed.
          evt.preventDefault();
          // Keep the combobox as the focus anchor even if the customer reached
          // an option with Tab before pressing Escape. Focus first so its focus
          // handler runs while the panel is still open, then close it.
          this.input.focus({ preventScroll: true });
          this.close();
          break;

        // no default
      }
    }

    /**
     * Handles 'keyup' events on the custom element.
     */
    handleKeyup() {
      // If search field is empty after key press, show the focused-empty
      // panel (if it has content) instead of closing outright.
      if (!this.getQuery().length && !this.openEmptyState()) this.close();
    }

    /**
     * Handles keyboard navigation of the results.
     * Index walk over every option in DOM order (products, then collections,
     * then the all-results row) — suggestion/popular chips are excluded
     * because they never carry the `predictive-search__item` class. Wraps at
     * both ends: ArrowDown with no selection goes to the first option,
     * ArrowUp with no selection goes to the LAST option (not a no-op), and
     * each arrow at the corresponding end wraps to the other end.
     * @param {string} key - Key pressed.
     */
    handleResultsNav(key) {
      const options = Array.from(this.querySelectorAll('.js-search-results .predictive-search__item'));
      if (!options.length) return;

      const selectedResult = options.find((option) => option.getAttribute('aria-selected') === 'true');
      const currentIndex = selectedResult ? options.indexOf(selectedResult) : -1;

      let nextIndex;
      if (key === 'ArrowDown') {
        nextIndex = currentIndex === -1 ? 0 : (currentIndex + 1) % options.length;
      } else if (key === 'ArrowUp') {
        nextIndex = currentIndex === -1 ? options.length - 1 : (currentIndex - 1 + options.length) % options.length;
      } else {
        return;
      }

      const resultToSelect = options[nextIndex];

      this.statusEl.textContent = '';

      // If the selected item didn't change, do nothing.
      if (resultToSelect === selectedResult) return;

      if (selectedResult) selectedResult.setAttribute('aria-selected', 'false');
      resultToSelect.setAttribute('aria-selected', 'true');

      this.setLiveRegionText(resultToSelect.textContent);
      this.input.setAttribute('aria-activedescendant', resultToSelect.id);

      // BG-223: keep the selection visible inside the panel's own scroller (never scrolls the page).
      const scroller = resultToSelect.closest('.predictive-search');
      if (scroller) {
        const box = resultToSelect.getBoundingClientRect();
        const view = scroller.getBoundingClientRect();
        if (box.bottom > view.bottom) scroller.scrollTop += box.bottom - view.bottom + 8;
        else if (box.top < view.top) scroller.scrollTop -= view.top - box.top + 8;
      }
    }

    /**
     * Selects a result. Every option IS the interactive element — product and
     * collection rows are `<a href>` anchors, the all-results row is a real
     * `<button type="submit">` — never a nested link inside a role="option"
     * wrapper (nesting interactive controls inside an ARIA option is an axe
     * "nested-interactive" violation, and was also a type="button" no-op
     * regression for the all-results row's own click behavior). Clicking the
     * anchor navigates directly; clicking the submit button re-enters
     * handleSubmit(), which already gates on `.js-submit` to let it through.
     */
    selectOption() {
      const selectedResult = this.querySelector('.predictive-search__item[aria-selected="true"]');
      if (!selectedResult) return false;

      selectedResult.click();
      return true;
    }

    /**
     * Gets the results for a search term.
     * @param {string} searchTerm - Search query.
     */
    async getResults(searchTerm) {
      const requestSerial = ++this.requestSerial;
      let searchFields = 'title,product_type,variants.title,vendor';
      if (theme.settings.pSearchIncludeSkus) searchFields += ',variants.sku';
      if (theme.settings.pSearchIncludeTags) searchFields += ',tag';

      let searchParams = '';
      if (this.productTypeInput && this.productTypeInput.value !== '') {
        searchParams = `q=product_type:${encodeURIComponent(this.productTypeInput.value)} AND ${encodeURIComponent(searchTerm)}`;
      } else {
        searchParams = `q=${encodeURIComponent(searchTerm)}`;
      }

      searchParams += `&${encodeURIComponent('resources[limit]')}=${theme.settings.pSearchLimit}`;
      searchParams += `&${encodeURIComponent('resources[limit_scope]')}=${
        theme.settings.pSearchLimitScope
      }`;
      searchParams += `&${encodeURIComponent('resources[options][fields]')}=${searchFields}`;
      searchParams += `&${encodeURIComponent('resources[type]')}=product,collection,query`;
      if (theme.settings.pSearchHideUnavailable) searchParams += `&${encodeURIComponent('resources[options][unavailable_products]')}=hide`;
      // BG-675: 'show' ranks sold-out products by match (an exact sold-out title comes first); 'last' (default) sends nothing, Shopify then lists them after every in-stock match.
      else if (theme.settings.pSearchUnavailableOrder === 'show') searchParams += `&${encodeURIComponent('resources[options][unavailable_products]')}=show`;
      searchParams += '&section_id=predictive-search';

      const queryKey = searchParams.replace(' ', '-').toLowerCase();
      if (this.cachedResults[queryKey]) {
        this.renderResults(this.cachedResults[queryKey]);
        return;
      }

      this.showLoadingState();

      try {
        const response = await fetch(`${theme.routes.predictiveSearch}?${searchParams}`);
        if (!response.ok) throw new Error(response.status);

        const responseText = await response.text();
        if (requestSerial !== this.requestSerial) return;

        const tmpl = document.createElement('template');
        tmpl.innerHTML = responseText;

        const resultsEl = tmpl.content.querySelector('#shopify-section-predictive-search');
        const resultsMarkup = resultsEl.innerHTML.replace(/psearch/g, this.input.id);

        this.cachedResults[queryKey] = resultsMarkup;
        this.renderResults(resultsMarkup);
      } catch (error) {
        if (requestSerial !== this.requestSerial) return;

        this.removeAttribute('loading');
        this.close();
        throw error;
      }
    }

    /**
     * Shows a compact visual loading state while the predictive-search request
     * is in flight. The existing live region announces the same localized text.
     */
    showLoadingState() {
      const panel = document.createElement('div');
      const indicator = document.createElement('span');
      const label = document.createElement('span');

      panel.className = 'predictive-search predictive-search--loading';
      panel.setAttribute('aria-hidden', 'true');
      indicator.className = 'predictive-search__spinner';
      label.textContent = this.loadingText;
      panel.append(indicator, label);

      this.results.replaceChildren(panel);
      if (this.emptyPanel) this.emptyPanel.hidden = true;
      this.input.setAttribute('aria-activedescendant', '');
      this.setAttribute('loading', '');
      this.setLiveRegionLoadingState();
      this.open();
    }

    /**
     * Sets the live region loading state.
     */
    setLiveRegionLoadingState() {
      this.setLiveRegionText(this.loadingText);
    }

    /**
     * Sets the live region text.
     * @param {string} statusText - Status text.
     */
    setLiveRegionText(statusText) {
      this.statusEl.setAttribute('aria-hidden', 'false');
      this.statusEl.textContent = statusText;

      setTimeout(() => {
        this.statusEl.setAttribute('aria-hidden', 'true');
      }, 1000);
    }

    /**
     * Renders the results markup.
     * @param {string} resultsMarkup - Results markup.
     */
    renderResults(resultsMarkup) {
      this.results.innerHTML = resultsMarkup;
      if (this.emptyPanel) this.emptyPanel.hidden = true;
      this.removeAttribute('loading');
      this.results.querySelectorAll('[role="option"]').forEach((option) => {
        option.setAttribute('aria-selected', 'false');
      });
      // Each keystroke replaces the innerHTML, so a previous selection's id
      // would otherwise dangle as a stale reference until the next arrow key.
      this.input.setAttribute('aria-activedescendant', '');
      this.setAttribute('results', true);
      this.open();
    }

    /**
     * Opens the results listbox.
     */
    open() {
      this.overlay.classList.add('is-visible');
      if (this.getQuery().length) this.resetBtn.hidden = false;
      this.input.setAttribute('aria-expanded', 'true');
      this.setAttribute('open', '');
      document.body.classList.add('overlay-predictive-search');

      // Add event handlers (so the bound event listeners can be removed).
      this.keydownHandler = this.keydownHandler || this.handleKeydown.bind(this);
      this.keyupHandler = this.keyupHandler || this.handleKeyup.bind(this);
      this.resetBtnClickHandler = this.resetBtnClickHandler || this.close.bind(this);
      this.overlayClickHandler = this.overlayClickHandler || this.close.bind(this);

      // Add event listeners (for while results are open).
      this.addEventListener('keydown', this.keydownHandler);
      this.addEventListener('keyup', this.keyupHandler);
      this.resetBtn.addEventListener('click', this.resetBtnClickHandler);
      this.overlay.addEventListener('click', this.overlayClickHandler);
    }

    /**
     * Closes the results listbox.
     * @param {object} evt - Event object.
     */
    close(evt) {
      // Invalidate any in-flight response so Escape/overlay/reset cannot be
      // followed by stale results reopening the panel.
      this.requestSerial += 1;

      // If reset button was clicked, empty the input field.
      if (evt && evt.target === this.resetBtn) {
        this.input.value = '';
        this.removeAttribute('results');
        this.input.focus();
      }

      // Deselect the selected result (if there is one).
      const selected = this.querySelector('.predictive-search__item[aria-selected="true"]');
      if (selected) selected.setAttribute('aria-selected', 'false');

      if (this.emptyPanel) this.emptyPanel.hidden = true;

      this.removeAttribute('open');
      this.resetBtn.hidden = true;
      this.overlay.classList.remove('is-visible');
      this.input.setAttribute('aria-activedescendant', '');
      this.input.setAttribute('aria-expanded', 'false');
      document.body.classList.remove('overlay-predictive-search');

      // Remove event listeners added on search results opening.
      this.removeEventListener('keydown', this.keydownHandler);
      this.removeEventListener('keyup', this.keyupHandler);
      this.resetBtn.removeEventListener('click', this.resetBtnClickHandler);
      this.overlay.removeEventListener('click', this.overlayClickHandler);
    }
  }

  customElements.define('predictive-search', PredictiveSearch);
}
